import { Graphics, InteractionEvent, Text } from "pixi.js"
import { TEXT_ROLES, TEXT_VARIANTS, TextRole, TextVariant, TypographyStyle } from "../text/typography"
import { TextRenderer } from "../text/text_renderer"
import { BUTTON_DISABLED_ALPHA, BUTTON_SIZES, ButtonSize, ButtonVariant, ButtonAppearance, ButtonShape, UI_RADII, UI_SPACING, UI_THEMES, UiTheme } from "./ui_tokens"
import { controlCssScale, controlHitRect, getUiMetrics, ResponsiveHitRect, TOUCH_TARGET_CSS } from "./ui_metrics"

export interface UiButtonProps {
    label: string
    onClick: () => void
    size?: ButtonSize
    theme?: UiTheme
    variant?: ButtonVariant
    width?: number
    height?: number
    // Measured layouts and legacy labels may override this; ordinary labels follow size.
    fontSize?: number
    // Compatibility for server-authored button labels; new UI uses the size tiers.
    textVariant?: TextVariant
    textRole?: TextRole
    // Optional world-input hover hold. Owners release it before removing an entire ancestor.
    mouseControlCallback?: (held: boolean) => void
    fill?: number
    enabled?: boolean
    appearance?: ButtonAppearance
    // Accent tone gives filled actions a bright background and contrasting caption.
    tone?: "neutral" | "accent"
    shape?: ButtonShape
    // Icon controls use a square footprint when dimensions are automatic.
    iconOnly?: boolean
    textColor?: number
    bold?: boolean
    accent?: number
    subtitle?: string
    // Controlled by the owner; clicking does not mutate selection behind its back.
    selected?: boolean
    // Chat uses press activation because closing the keyboard moves the canvas before release.
    activation?: "release" | "press"
    // Explicit dimensions are for measured layouts. Limit extra hit padding to avoid neighboring
    // controls overlapping; automatic dimensions can grow to the shared touch target instead.
    hitPadding?: number | { x: number; y: number }
}

// Shared action control. Release activation handles mouse, pen and touch by default.
export class UiButton extends Graphics {
    private labelText: Text
    private subtitleText?: Text
    private appearance: ButtonAppearance
    private tone: "neutral" | "accent"
    private shape: ButtonShape
    private iconOnly: boolean
    private selected: boolean
    private accent?: number
    private enabled: boolean
    private mouseControlCallback?: (held: boolean) => void
    private holdingMouseControl = false
    private hovered = false
    private pressed = false
    private autoWidth: boolean
    private autoHeight: boolean
    private baseFontSize: number
    private autoFontSize: boolean
    private hitPadding: number | { x: number; y: number }
    private buttonWidth: number
    private buttonHeight: number
    private metrics: typeof BUTTON_SIZES[ButtonSize]
    private theme: typeof UI_THEMES[UiTheme]
    private variant: ButtonVariant
    private backgroundFill?: number

    constructor({
        label, onClick, size = "medium", theme = "slate", variant = "secondary",
        width, height, fontSize, textVariant, textRole, mouseControlCallback, fill, enabled = true, hitPadding = 2,
        appearance = "filled", tone = "neutral", shape = "rounded", iconOnly = false,
        textColor, bold, accent, subtitle, selected = false, activation = "release",
    }: UiButtonProps) {
        super()
        this.appearance = appearance
        this.tone = appearance === "filled" ? tone : "neutral"
        this.shape = shape
        this.iconOnly = iconOnly
        this.selected = selected
        this.accent = accent
        this.metrics = BUTTON_SIZES[size]
        this.theme = UI_THEMES[theme]
        this.variant = variant
        this.backgroundFill = fill
        this.enabled = enabled
        this.mouseControlCallback = mouseControlCallback
        this.autoWidth = width === undefined
        this.autoHeight = height === undefined
        const variantStyle: TypographyStyle | undefined = textRole ? TEXT_ROLES[textRole] : textVariant ? TEXT_VARIANTS[textVariant] : undefined
        this.baseFontSize = fontSize ?? variantStyle?.fontSize ?? this.metrics.fontSize
        this.autoFontSize = fontSize === undefined && textVariant === undefined && textRole === undefined && height === undefined
        this.hitPadding = hitPadding
        const labelRenderer = new TextRenderer().role(this.metrics.labelRole).fontSize(this.baseFontSize)
            .color(textColor ?? (this.tone === "accent" ? this.theme.onAccent : this.theme.text)).strokeThickness(0)
        if (textRole) labelRenderer.role(textRole)
        else if (textVariant) labelRenderer.variant(textVariant)
        labelRenderer.bold(bold ?? variantStyle?.bold ?? false)
        this.labelText = labelRenderer.render(label) as Text
        this.addChild(this.labelText)
        if (subtitle !== undefined) {
            this.subtitleText = new TextRenderer().role(this.metrics.subtitleRole)
                .color(textColor ?? (this.tone === "accent" ? this.theme.onAccent : this.theme.text)).strokeThickness(0).render(subtitle) as Text
            this.subtitleText.alpha = 0.7
            this.addChild(this.subtitleText)
        }
        this.buttonWidth = width ?? this.labelText.width + this.metrics.paddingX * 2
        this.buttonHeight = height ?? this.metrics.height
        this.setEnabled(enabled)
        this.hitArea = new ResponsiveHitRect(() => controlHitRect(this, this.buttonWidth, this.buttonHeight,
            this.autoWidth && this.autoHeight ? Infinity : this.hitPadding))
        this.on("added", () => this.draw())
        this.on("removed", () => {
            // Ancestor removal does not reach children; owners still release before detaching a stack.
            this.releaseMouseControl()
            this.hovered = false
            this.pressed = false
        })

        this.on("pointerover", this.onHover)
        this.on("mouseover", this.onHover)
        this.on("pointerout", this.resetInteraction)
        this.on("mouseout", this.resetInteraction)
        this.on("pointerdown", (event: InteractionEvent) => {
            event?.stopPropagation()
            if (!this.enabled || this.secondaryPointer(event)) return
            this.pressed = true
            this.draw()
            if (activation === "press") onClick()
        })
        this.on("pointerup", () => { this.pressed = false; this.draw() })
        for (const event of ["pointerupoutside", "pointercancel"]) {
            this.on(event, this.resetInteraction)
        }
        this.on("pointertap", (event: InteractionEvent) => {
            event?.stopPropagation()
            if (activation === "release" && this.enabled && !this.secondaryPointer(event)) onClick()
        })
    }

    private onHover = (event?: InteractionEvent) => {
        if (!this.enabled) return
        this.hovered = true
        if (event?.data?.pointerType !== "touch") this.setMouseControl(true)
        this.draw()
    }

    private setMouseControl = (held: boolean) => {
        if (!this.mouseControlCallback || this.holdingMouseControl === held) return
        this.holdingMouseControl = held
        this.mouseControlCallback(held)
    }

    // Release only a hold acquired by this button; safe when an owner tears down a whole stack.
    releaseMouseControl = () => this.setMouseControl(false)

    resetInteraction = () => {
        this.releaseMouseControl()
        this.hovered = false
        this.pressed = false
        this.draw()
    }

    setSelected = (selected: boolean) => {
        this.selected = selected
        this.draw()
    }

    isSelected = () => this.selected
    getLabel = () => this.labelText.text
    getBoxSize = () => ({ width: this.buttonWidth, height: this.buttonHeight })

    private secondaryPointer = (event?: InteractionEvent) =>
        event?.data?.pointerType !== "touch" && (event?.data?.button ?? 0) > 0

    resize = (width: number, height: number) => {
        this.autoWidth = false
        this.autoHeight = false
        this.autoFontSize = false
        this.buttonWidth = width
        this.buttonHeight = height
        this.draw()
    }

    setLabel = (label: string) => {
        this.labelText.text = label
        if (this.autoWidth) this.buttonWidth = this.labelText.width + this.metrics.paddingX * 2
        this.draw()
    }

    setEnabled = (enabled: boolean) => {
        this.releaseMouseControl()
        this.enabled = enabled
        this.interactive = enabled
        this.interactiveChildren = false
        this.cursor = enabled ? "pointer" : "default"
        this.alpha = enabled ? 1 : BUTTON_DISABLED_ALPHA
        this.hovered = false
        this.pressed = false
        this.draw()
    }

    refreshMetrics = () => this.draw()

    private draw = () => {
        const scale = controlCssScale(this)
        const touch = getUiMetrics().touch
        if (this.autoFontSize) {
            const fontScale = touch ? Math.min(1, scale.x, scale.y) : 1
            this.labelText.style.fontSize = this.baseFontSize / fontScale
            if (this.subtitleText) this.subtitleText.style.fontSize = this.metrics.subtitleFontSize / fontScale
        }
        const contentHeight = this.labelText.height + (this.subtitleText ? this.subtitleText.height + UI_SPACING.xs : 0)
        if (this.autoHeight) this.buttonHeight = Math.max(this.metrics.height, contentHeight + UI_SPACING.sm, touch ? TOUCH_TARGET_CSS / scale.y : 0)
        if (this.autoWidth) this.buttonWidth = this.iconOnly ? this.buttonHeight
            : Math.max(Math.max(this.labelText.width, this.subtitleText?.width ?? 0) + this.metrics.paddingX * 2, touch ? TOUCH_TARGET_CSS / scale.x : 0)
        if (this.autoWidth && this.iconOnly && touch) this.buttonWidth = Math.max(this.buttonWidth, TOUCH_TARGET_CSS / scale.x)
        const accent = this.accent ?? (this.variant === "success" ? this.theme.success
            : this.variant === "danger" ? this.theme.danger : this.theme.accent)
        const border = this.hovered || this.selected || this.variant !== "secondary" ? accent : this.theme.border
        this.clear()
        const active = (this.pressed && this.enabled) || this.selected
        const fill = this.tone === "accent" ? this.backgroundFill ?? accent : active ? this.theme.pressedFill
            : this.hovered && this.enabled ? this.theme.hoverFill : this.backgroundFill ?? this.theme.fill
        const fillAlpha = this.appearance === "filled" || active || this.hovered ? 1 : 0
        this.beginFill(fill, fillAlpha)
        this.lineStyle(this.appearance === "subtle" && !active && !this.hovered ? 0 : this.tone === "accent" && (active || this.hovered) ? 2 : 1, border)
        const radius = this.shape === "rounded" ? UI_RADII.control : Math.min(this.buttonWidth, this.buttonHeight) / 2
        if (this.shape === "circle") this.drawCircle(this.buttonWidth / 2, this.buttonHeight / 2, radius)
        else this.drawRoundedRect(0, 0, this.buttonWidth, this.buttonHeight, radius)
        this.endFill()
        this.labelText.position.set((this.buttonWidth - this.labelText.width) / 2, (this.buttonHeight - contentHeight) / 2)
        if (this.subtitleText) this.subtitleText.position.set((this.buttonWidth - this.subtitleText.width) / 2,
            this.labelText.y + this.labelText.height + UI_SPACING.xs)
        if (this.hitArea instanceof ResponsiveHitRect) this.hitArea.refresh()
    }

    destroy(options?: any) {
        this.releaseMouseControl()
        if (!this.labelText.destroyed) this.labelText.destroy({ texture: true, baseTexture: true })
        if (this.subtitleText && !this.subtitleText.destroyed) this.subtitleText.destroy({ texture: true, baseTexture: true })
        super.destroy(options)
    }
}
